/**
 * Item connections — edges for one item, with peer creation provenance.
 */

import { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import {
  AppCard,
  EmptyState,
  Eyebrow,
  SecondaryButton,
  StatusChip,
} from '../components/primitives';
import { CreationProvenanceBadge } from '../../components/prioritization/provenance/CreationProvenanceBadge';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore';
import { useNexusRelationshipStore } from '../../stores/prioritization/nexusRelationshipStore';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { getItemCountState } from '../../components/collection/types';

export default function RelationshipsItemScreen() {
  const app = getAppDefinition('relationships')!;
  const { itemId = '' } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const item = useItemsStore((s) => s.items.find((i) => i.id === itemId));
  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const fetchRelationships = useItemRelationshipsStore((s) => s.fetchFromSupabase);
  const edges = useItemRelationshipsStore((s) =>
    itemId ? s.edges.filter((e) => e.sourceItemId === itemId || e.targetItemId === itemId) : [],
  );

  const workspaceId = getCurrentWorkspaceId() ?? '';
  const nexusProposals = useNexusRelationshipStore((s) =>
    workspaceId ? (s.byWorkspace[workspaceId]?.proposals ?? []) : [],
  );
  const fetchProposals = useNexusRelationshipStore((s) => s.fetchProposals);

  useEffect(() => {
    if (!itemId) {
      navigate('/apps/relationships', { replace: true });
      return;
    }
    void fetchItems();
    void fetchRelationships();
    if (workspaceId) void fetchProposals(workspaceId);
  }, [itemId, fetchItems, fetchRelationships, fetchProposals, workspaceId, navigate]);

  const itemById = useMemo(() => {
    const map = new Map(items.map((i) => [i.id, i]));
    return map;
  }, [items]);

  const pendingForItem = useMemo(
    () =>
      nexusProposals.filter(
        (p) => p.sourceItemId === itemId || p.targetItemId === itemId,
      ),
    [nexusProposals, itemId],
  );

  const activeEdges = edges.filter((e) => e.status === 'active' || e.status === 'confirmed');
  const countState = getItemCountState(activeEdges.length);

  if (!itemId) {
    return null;
  }

  if (!item) {
    return (
      <AppShell app={app} backTo="/apps/relationships" backLabel="Back to suggestions">
        <EmptyState
          title="Item not found"
          hint="It may have been removed or is outside this workspace."
          action={
            <SecondaryButton onClick={() => navigate('/apps/relationships')}>
              Back to suggestions
            </SecondaryButton>
          }
        />
      </AppShell>
    );
  }

  return (
    <AppShell app={app} backTo="/apps/relationships" backLabel="Back to suggestions">
      <ScreenHeading
        title={item.title}
        subtitle="Confirmed connections and any AI suggestions still open for this item."
      />
      <BallLine ballWith="you — inspect links; accept suggestions from the queue" />

      <div className="space-y-4">
        <AppCard>
          <Eyebrow>This item</Eyebrow>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <CreationProvenanceBadge
              source={item.creationSource}
              reviewStatus={item.creationReviewStatus}
            />
            {item.classificationStatus && (
              <StatusChip
                label={`Classification: ${item.classificationStatus}`}
                tone={item.classificationStatus === 'approved' ? 'green' : 'mustard'}
              />
            )}
          </div>
        </AppCard>

        {pendingForItem.length > 0 && (
          <AppCard>
            <Eyebrow>{pendingForItem.length} open AI suggestions</Eyebrow>
            <ul className="mt-2 space-y-2 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
              {pendingForItem.map((p) => {
                const otherId =
                  p.sourceItemId === itemId ? p.targetItemId : p.sourceItemId;
                const other = itemById.get(otherId);
                return (
                  <li key={p.id} className="flex flex-wrap items-center gap-2">
                    <StatusChip label="AI-generated" tone="slate" />
                    <StatusChip label={p.relationshipType} tone="neutral" />
                    <span>{other?.title ?? otherId}</span>
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 font-body text-xs text-gray-500">
              <Link
                to="/apps/relationships"
                className="text-primary-600 hover:underline dark:text-primary-400"
              >
                Review on suggestions queue →
              </Link>
            </p>
          </AppCard>
        )}

        <AppCard>
          <Eyebrow>
            {activeEdges.length} connection{activeEdges.length === 1 ? '' : 's'}
          </Eyebrow>
          {countState === 'zero' ? (
            <div className="mt-3">
              <EmptyState
                title="No confirmed connections yet"
                hint="Accepted Nexus suggestions and human-declared edges appear here."
              />
            </div>
          ) : (
            <ul className="mt-3 divide-y divide-gray-100 dark:divide-[var(--rule)]">
              {activeEdges.map((edge) => {
                const otherId =
                  edge.sourceItemId === itemId ? edge.targetItemId : edge.sourceItemId;
                const other = itemById.get(otherId);
                return (
                  <li key={edge.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <StatusChip label={edge.type} tone="neutral" />
                        <StatusChip label={edge.status} tone="green" />
                      </div>
                      <p className="mt-1 truncate font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">
                        <Link
                          to={`/apps/relationships/${otherId}`}
                          className="hover:underline"
                        >
                          {other?.title ?? otherId}
                        </Link>
                      </p>
                      <div className="mt-1.5">
                        <CreationProvenanceBadge
                          source={other?.creationSource}
                          reviewStatus={other?.creationReviewStatus}
                          compact
                        />
                      </div>
                    </div>
                    <Link
                      to={`/prioritization/items/${otherId}`}
                      className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-gray-400 hover:text-primary-600"
                    >
                      Open item →
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </AppCard>

        <p className="font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
          Platform:{' '}
          <Link
            to={`/prioritization/items/${itemId}`}
            className="text-primary-600 hover:underline dark:text-primary-400"
          >
            Item detail
          </Link>
          {' · '}
          <Link
            to="/prioritization/relationships"
            className="text-primary-600 hover:underline dark:text-primary-400"
          >
            Relationships home
          </Link>
        </p>
      </div>
    </AppShell>
  );
}
